import type { FC, FormEvent } from 'react'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

import { Marquee } from '@/components/generals/marquee'
import { footerMarquee, heroMarquee, navLinks, studio } from '@/constants/dentea'

export const Footer: FC = () => {
    const [email, setEmail] = useState('')
    const [subscribed, setSubscribed] = useState(false)

    const onSubscribe = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (!email.trim()) return

        setSubscribed(true)
        setEmail('')
    }

    return (
        <footer className='relative w-full overflow-hidden bg-paper'>
            <div className='flex w-full flex-col items-center gap-24 px-6 py-24 md:gap-40 md:px-12'>
                <p
                    data-anim='lines'
                    className='display mx-auto max-w-[16ch] text-center text-[3rem] opacity-0 sm:text-[5rem] lg:text-[7rem]'
                >
                    {footerMarquee}.
                </p>

                <div className='w-full'>
                    <Link href='/' aria-label={`${studio.name} home`} className='block'>
                        <span className='display display-italic block w-full text-center text-[26vw] leading-[0.75] text-ink'>
                            {studio.name}
                        </span>
                    </Link>
                </div>

                <div className='grid w-full max-w-[1200px] grid-cols-2 gap-12 md:grid-cols-4'>
                    <nav aria-label='Footer' className='flex flex-col gap-3'>
                        <span className='label text-ink/40'>Pages</span>

                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className='label text-ink transition-opacity duration-300 hover:opacity-55'
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    <div className='flex flex-col gap-3'>
                        <span className='label text-ink/40'>Social</span>

                        {studio.social.map((item) => (
                            <a
                                key={item.label}
                                href={item.href}
                                target='_blank'
                                rel='noreferrer'
                                className='label text-ink transition-opacity duration-300 hover:opacity-55'
                            >
                                {item.label}
                            </a>
                        ))}
                    </div>

                    <div className='flex flex-col gap-3'>
                        <span className='label text-ink/40'>Legal</span>

                        {studio.legal.map((item) => (
                            <Link
                                key={item.label}
                                href={item.href}
                                className='label text-ink transition-opacity duration-300 hover:opacity-55'
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>

                    <div className='flex flex-col gap-3'>
                        <span className='label text-ink/40'>Newsletter</span>

                        <form onSubmit={onSubscribe} className='flex flex-col gap-3'>
                            <label htmlFor='footer-email' className='label text-ink'>
                                Subscribe to the studio journal
                            </label>

                            <div className='flex items-center gap-2 rounded-full bg-ink/6 p-1 pl-4'>
                                <input
                                    id='footer-email'
                                    type='email'
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder='you@email.com'
                                    aria-label='Email address'
                                    className='min-w-0 flex-1 bg-transparent py-2 text-sm text-ink outline-none placeholder:text-ink/45'
                                />

                                <button
                                    type='submit'
                                    className='label flex shrink-0 items-center gap-1.5 rounded-full bg-ink py-2 pr-3 pl-4 text-paper transition-colors duration-500 hover:bg-charcoal'
                                >
                                    Submit
                                    <ArrowRight aria-hidden='true' className='size-3.5' />
                                </button>
                            </div>

                            <p role='status' aria-live='polite' className='label min-h-4 text-ink/50'>
                                {subscribed ? 'Thank you, you are on the list.' : ''}
                            </p>
                        </form>
                    </div>
                </div>

                <div className='flex w-full max-w-[1200px] flex-col items-center justify-between gap-4 border-t border-ink/10 pt-8 md:flex-row'>
                    <p className='label text-ink/50'>{studio.copyright}</p>

                    <p className='label text-ink/50'>
                        Built in Modulify by{' '}
                        <a
                            href='https://snowy.framer.website/'
                            target='_blank'
                            rel='noreferrer'
                            className='text-ink transition-opacity hover:opacity-60'
                        >
                            Snowy Atiq
                        </a>
                    </p>
                </div>
            </div>

            <Marquee
                text={heroMarquee}
                speed={44}
                separator={false}
                className='border-t border-ink/10 py-5'
                itemClassName='label text-ink/45'
            />
        </footer>
    )
}
